import { Form, Input, InputRef, Row, Select, Spin } from 'antd';
import Modal from 'AppointmentBox/Modal';
import { AxiosResponse } from 'axios';
import DateMonthInput from 'components/BirthdayInput';
import PhoneInput from 'components/PhoneInput';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import shopActions from 'services/actions/shop';
import apis from 'services/apis';
import { IPayloadAddNewCustomer } from 'services/types/request';
import { IAddNewCustomerResData } from 'services/types/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { ICustomerUI } from 'types/customer';
import { formatPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';

interface IAddCustomerFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  birthday: string;
  gender: string;
}

type Props = {};
export type IAddCustomerRef = {
  setPhone: (value: string) => void;
  open: ({ phoneNumber, requireFirstName }: { phoneNumber?: string; requireFirstName?: boolean }) => Promise<ICustomerUI>;
};
export const addCustomerRef = React.createRef<IAddCustomerRef>();
const AddCustomerUI = forwardRef<IAddCustomerRef, Props>(({ }, ref) => {
  const inputRef = useRef<InputRef>(null);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [requireName, setRequireName] = useState(false);
  const resolveCallback = useRef<(data: ICustomerUI) => void>(() => undefined);

  const handleFinish = async (_values: any) => {
    const values = _values as IAddCustomerFormValues;
    const body: IPayloadAddNewCustomer = {
      birthDay: values.birthday,
      email: values.email,
      firstName: values.firstName || 'WALK-IN',
      lastName: values.lastName,
      gender: values.gender,
      phone: values.phoneNumber?.replace(/[^0-9]/g, '')
    };
    setLoading(true);
    try {
      const res: AxiosResponse<{ payload: IAddNewCustomerResData | null }> = await apis.addNewCustomer(storage.shopId.get(), body);
      const data = res?.data?.payload;
      if (data) {
        const newCus: ICustomerUI = {
          id: data.id?.toString(),
          name: data.name,
          phone: data.phone,
        };
        dispatch(shopActions.afterCreateCustomer(newCus));
        resolveCallback.current(newCus);
        handleClose();
      }
    } catch (error) { } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  useImperativeHandle(ref, () => ({
    setPhone: (value) => form.setFieldValue('phoneNumber', formatPhone(value)),
    open: async ({ phoneNumber, requireFirstName }: { phoneNumber?: string; requireFirstName?: boolean }) => {
      if (phoneNumber) form.setFieldValue('phoneNumber', formatPhone(phoneNumber));
      setRequireName(!!requireFirstName);
      setOpen(true);
      return new Promise<ICustomerUI>((resolve) => {
        resolveCallback.current = resolve;
      });
    }
  }));

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        onOk={() => form.submit()}
        title="ADD CUSTOMER"
        width={'60vw'}
        height={'auto'}
        okText="ADD"
        disabledOk={loading}
      >
        <Spin spinning={loading}>
          <FormStyled form={form} layout='vertical' size='large' onFinish={handleFinish} validateTrigger={['onSubmit']}>
            <Row className='group'>
              <Form.Item className='col-item' name={'firstName'} label={'FIRST NAME'}
                rules={requireName ? [{ required: true, message: 'Please enter customer name' }] : undefined}
              >
                <Input ref={inputRef} placeholder='WALK-IN' />
              </Form.Item>
              <Form.Item className='col-item' name={'lastName'} label={'LAST NAME'}
              >
                <Input placeholder='Please enter' />
              </Form.Item>
            </Row>
            <Row className='group'>
              <Form.Item className='col-item' name={'phoneNumber'} label={'PHONE NUMBER'} rules={[{ required: true }]}
              >
                <PhoneInput />
              </Form.Item>
              <Form.Item className='col-item' name={'email'} label={'EMAIL'} rules={[{ type: 'email' }]}>
                <Input placeholder='Please enter' />
              </Form.Item>
            </Row>
            <Row className='group'>
              <Form.Item className='col-item' name={'birthday'} label={'BIRTHDAY'}>
                <DateMonthInput />
              </Form.Item>
              <Form.Item className='col-item' name={'gender'} label={'GENDER'}>
                <Select
                  defaultValue="none"
                  options={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'none', label: 'None' },
                  ]}
                />
              </Form.Item>
            </Row>
          </FormStyled>
        </Spin>
      </Modal>
    </>
  );
});

export { AddCustomerUI };

const AddCustomer = {
  add: (data: { phoneNumber?: string; requireFirstName?: boolean }) => addCustomerRef.current?.open(data),
};

export default AddCustomer;

AddCustomerUI.displayName = 'AddCustomerUI';

const FormStyled = styled(Form)`
  padding: 1rem 1.5rem;
  .group {
    gap: 1.5rem;
  }
  .col-item {
    flex:1;
  }
  input.ant-input {
    line-height: 2;
    &::placeholder {
      color:#dadada;
    }
  }
  .ant-select-single.ant-select-lg {
    height: 46px;
  }
  &.ant-form-large .ant-form-item .ant-form-item-label>label {
    height: 24px;
  }
`;
